import usePageTitle from "../../core/hooks/UsePageTitle";
import DefaultLayout from "../layouts/default/Default.layout";
import { Col, Form, Input, Row, Button, notification, Modal, Select } from "antd";
import { Usuario } from "../../sdk/@types";
import useUsuario from "../../core/hooks/UseUsuario";
import { useEffect, useRef, useState } from "react";
import UsuarioService from "../../sdk/services/UsuarioService";
import ResolucaoService from "../../sdk/services/ResolucaoService";
import { Player } from "@lottiefiles/react-lottie-player";
import deletedSuccessfullyAnim from "../../assets/animations/deleted-successfully.json";

const profiles = [
  { id: "administrador", name: "Administrador" },
  { id: "basico", name: "Básico" },
  { id: "master", name: "Master" },
];

interface SubjectHistory {
  subjectId: string;
  subjectName: string;
  total: number;
  correct: number;
  wrong: number;
}

export default function PerfilView() {
  const [form] = Form.useForm<Usuario.Usuario>();
  usePageTitle("Perfil");
  const { usuario } = useUsuario();

  const [open, setOpen] = useState(false);
  const [disabled, setDisabled] = useState(true);
  const [bounds, setBounds] = useState({ left: 0, top: 0, bottom: 0, right: 0 });
  const draggleRef = useRef<HTMLDivElement>(null);

  const [subjectHistory, setSubjectHistory] = useState<SubjectHistory[]>([]);
  const [loadingHistory, setLoadingHistory] = useState(true);
  const [clearing, setClearing] = useState<string | null>(null);
  const [successSubject, setSuccessSubject] = useState<string | null>(null);
  const [animKey, setAnimKey] = useState(0);

  useEffect(() => {
    if (usuario) form.setFieldsValue(usuario);
  }, [usuario]);

  useEffect(() => {
    ResolucaoService.getSubjectHistory()
      .then(res => { if (res) setSubjectHistory(res); })
      .finally(() => setLoadingHistory(false));
  }, []);

  const showModal = () => setOpen(true);

  const handleOk = async () => {
    const newPassword     = (document.getElementById("new-password")     as HTMLInputElement).value;
    const confirmPassword = (document.getElementById("confirm-password") as HTMLInputElement).value;

    if (newPassword !== confirmPassword) {
      notification.error({ message: "Erro", description: "As senhas não coincidem." });
      return;
    }

    const passwordPattern = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]{8,}$/;
    if (!passwordPattern.test(newPassword)) {
      notification.error({
        message: "Erro",
        description: "A senha deve ter pelo menos 8 caracteres, incluindo maiúscula, minúscula, número e caractere especial.",
      });
      return;
    }

    const mensagem = await UsuarioService.setPassword(newPassword);
    if (!mensagem) {
      notification.error({ message: "Erro", description: "Ocorreu um erro ao alterar a senha." });
      return;
    }

    notification.success({ message: "Sucesso", description: mensagem.message });
    setOpen(false);
  };

  const handleCancel = () => setOpen(false);

  const handleClear = (subjectId: string, subjectName: string) => {
    Modal.confirm({
      title: "Excluir histórico",
      content: "Deseja realmente excluir o seu histórico? Esta ação é irreversível",
      okText: "Excluir",
      okButtonProps: { danger: true },
      cancelText: "Cancelar",
      onOk: async () => {
        setClearing(subjectId);
        try {
          await ResolucaoService.clearSubjectHistory(subjectId);
          setSubjectHistory(prev => prev.filter(s => s.subjectId !== subjectId));
          setSuccessSubject(subjectName);
          setAnimKey(k => k + 1);
        } catch {
          notification.error({
            message: "Erro",
            description: "Não foi possível apagar o histórico. Tente novamente.",
          });
        } finally {
          setClearing(null);
        }
      },
    });
  };

  return (
    <DefaultLayout>
      <div className="min-h-full bg-slate-50 dark:bg-gray-800 p-6 space-y-6">

        {/* ── Dados pessoais ────────────────────────────────────────────── */}
        <div className="bg-white dark:bg-gray-900 rounded-2xl border border-slate-200 dark:border-slate-700 overflow-hidden">
          <div className="px-5 py-3 bg-slate-50 dark:bg-gray-800 border-b border-slate-200 dark:border-slate-700">
            <h3 className="text-sm font-semibold text-slate-700 dark:text-slate-200">Dados da conta</h3>
          </div>
          <div className="p-5">
            <Form form={form} layout="vertical" autoComplete="off">
              <Row gutter={16}>
                <Col span={9}>
                  <Form.Item label="Usuário" name={["userName"]}>
                    <Input disabled placeholder="Ex.: João" maxLength={100} />
                  </Form.Item>
                </Col>
                <Col span={15}>
                  <Form.Item label="Nome Completo" name={["fullName"]}>
                    <Input disabled placeholder="Ex.: João da Silva" maxLength={60} />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item label="Perfil" name={["profile"]}>
                    <Select disabled showSearch optionFilterProp="children"
                      filterOption={(input, option) =>
                        (option!.children as unknown as string).includes(input)
                      }>
                      {profiles.map(p => (
                        <Select.Option key={p.id} value={p.id}>{p.name}</Select.Option>
                      ))}
                    </Select>
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item label="E-mail" name={["email"]}>
                    <Input disabled placeholder="Ex.: joao.silva@example.com" maxLength={254} />
                  </Form.Item>
                </Col>
                <Col span={24}>
                  <Button type="primary" onClick={showModal}>Alterar Senha</Button>
                </Col>
              </Row>
            </Form>
          </div>
        </div>

        {/* ── Histórico por matéria ─────────────────────────────────────── */}
        <div className="bg-white dark:bg-gray-900 rounded-2xl border border-slate-200 dark:border-slate-700 overflow-hidden">
          <div className="flex items-center justify-between px-5 py-3 bg-slate-50 dark:bg-gray-800 border-b border-slate-200 dark:border-slate-700">
            <div>
              <h3 className="text-sm font-semibold text-slate-700 dark:text-slate-200">Histórico por matéria</h3>
              <p className="text-xs text-slate-400 dark:text-slate-400 mt-0.5">Apenas questões de treino — simulados não são afetados</p>
            </div>
            <span className="text-xs font-medium text-slate-500 dark:text-slate-400">
              {subjectHistory.length} {subjectHistory.length === 1 ? "matéria" : "matérias"}
            </span>
          </div>

          <div className="p-5">
            {loadingHistory ? (
              <div className="flex items-center gap-2 text-slate-400 dark:text-slate-400 text-sm py-6 justify-center">
                <svg className="animate-spin" width="16" height="16" viewBox="0 0 24 24" fill="none">
                  <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2" strokeOpacity="0.3" />
                  <path d="M12 2a10 10 0 0 1 10 10" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                </svg>
                Carregando...
              </div>
            ) : subjectHistory.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-10 gap-2 text-slate-400 dark:text-slate-400">
                <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                  strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M2 3h6a4 4 0 0 1 4 4v14a3 3 0 0 0-3-3H2z" />
                  <path d="M22 3h-6a4 4 0 0 0-4 4v14a3 3 0 0 1 3-3h7z" />
                </svg>
                <p className="text-sm">Nenhuma questão de treino respondida ainda.</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-slate-200 dark:border-slate-700">
                      {["Matéria", "Respondidas", "Acertos", "Erros", "Taxa", ""].map(h => (
                        <th key={h}
                          className="py-2 px-3 text-left text-[11px] font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wide whitespace-nowrap">
                          {h}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {subjectHistory.map(row => {
                      const pct = row.total > 0 ? Math.round((row.correct / row.total) * 100) : 0;
                      const isClearing = clearing === row.subjectId;
                      return (
                        <tr key={row.subjectId}
                          className="border-b border-slate-100 dark:border-slate-700 last:border-0 hover:bg-slate-50 dark:hover:bg-gray-800 transition-colors">

                          {/* Matéria */}
                          <td className="py-3 px-3 font-medium text-slate-800 dark:text-slate-100 max-w-[220px]">
                            <span className="truncate block">{row.subjectName || "—"}</span>
                          </td>

                          {/* Total */}
                          <td className="py-3 px-3 text-slate-600 dark:text-slate-300 text-center">{row.total}</td>

                          {/* Acertos */}
                          <td className="py-3 px-3 text-center">
                            <span className="font-semibold" style={{ color: "#3d9b6e" }}>{row.correct}</span>
                          </td>

                          {/* Erros */}
                          <td className="py-3 px-3 text-center">
                            <span className="font-semibold" style={{ color: "#8884d8" }}>{row.wrong}</span>
                          </td>

                          {/* Taxa */}
                          <td className="py-3 px-3 text-center">
                            <span className={`
                              inline-block text-xs font-semibold px-2.5 py-0.5 rounded-full
                              ${pct >= 70 ? "bg-green-50 text-green-700" :
                                pct >= 50 ? "bg-amber-50 text-amber-700" :
                                            "bg-red-50 text-red-600"}
                            `}>
                              {pct}%
                            </span>
                          </td>

                          {/* Ação */}
                          <td className="py-3 px-3 text-right">
                            <button
                              onClick={() => handleClear(row.subjectId, row.subjectName)}
                              disabled={isClearing}
                              className="inline-flex items-center gap-1.5 text-xs font-medium text-red-500
                                hover:text-red-700 transition-colors disabled:opacity-40 disabled:cursor-not-allowed cursor-pointer"
                            >
                              {isClearing ? (
                                <>
                                  <svg className="animate-spin" width="12" height="12" viewBox="0 0 24 24" fill="none">
                                    <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2" strokeOpacity="0.3" />
                                    <path d="M12 2a10 10 0 0 1 10 10" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                                  </svg>
                                  Apagando...
                                </>
                              ) : (
                                <>
                                  <svg width="13" height="13" viewBox="0 0 24 24" fill="none"
                                    stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                    <polyline points="3 6 5 6 21 6" />
                                    <path d="M19 6l-1 14H6L5 6" />
                                    <path d="M10 11v6M14 11v6" />
                                    <path d="M9 6V4h6v2" />
                                  </svg>
                                  Limpar
                                </>
                              )}
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>

      </div>

      {/* Delete success modal */}
      <Modal
        open={!!successSubject}
        footer={null}
        onCancel={() => setSuccessSubject(null)}
        centered
        width={300}
        closable={false}
      >
        <div className="flex flex-col items-center gap-3 py-4">
          <Player
            autoplay
            keepLastFrame
            key={animKey}
            src={deletedSuccessfullyAnim as object}
            style={{ height: 160, width: 160 }}
          />
          <p className="font-semibold text-slate-700 dark:text-slate-200 text-center text-base">
            Histórico apagado com sucesso!
          </p>
          <p className="text-slate-400 dark:text-slate-400 text-sm text-center">
            Resoluções de treino de <strong className="text-slate-600 dark:text-slate-300">"{successSubject}"</strong> foram removidas.
          </p>
          <button
            onClick={() => setSuccessSubject(null)}
            className="mt-2 px-6 py-2 rounded-lg bg-lime-400 text-slate-900 text-sm font-semibold hover:bg-lime-500 transition-colors cursor-pointer"
          >
            Fechar
          </button>
        </div>
      </Modal>

      {/* Change password modal */}
      <Modal
        title={
          <div
            style={{ width: "100%", cursor: "move" }}
            onMouseOver={() => { if (disabled) setDisabled(false); }}
            onMouseOut={() => setDisabled(true)}
            onFocus={() => {}}
            onBlur={() => {}}
          >
            Informe a nova senha
          </div>
        }
        open={open}
        onOk={handleOk}
        onCancel={handleCancel}
      >
        <label htmlFor="new-password">Nova Senha</label>
        <Input.Password id="new-password" placeholder="Informe a nova senha"
          maxLength={60} style={{ marginBottom: "10px" }} />
        <label htmlFor="confirm-password">Confirme a Nova Senha</label>
        <Input.Password id="confirm-password" placeholder="Confirme a nova senha"
          maxLength={60} style={{ marginBottom: "10px" }} />
      </Modal>
    </DefaultLayout>
  );
}
